import {useLayoutEffect, useRef, useState} from 'react';

const GRID_WIDTH_BREAKPOINT = 260;
const OPTION_NON_CONTENT_WIDTH = 112; // padding, letter wrapper, eliminate button
// styled in multiple-choice-question.scss
const MATH_OVERFLOW_CLASS = 'mcq-math-overflow';

/**
 * **THIS IS A TEMPORARY WORKAROUND UNTIL MATHJAX SUPPORTS AUTO CONTENT WRAPPING IN V4**
 *
 * Hook to detect if any MathJax content within a container would overflow
 * in a multi-column grid layout.
 *
 * This is used to conditionally switch MCQ options to single-column layout
 * when MathJax expressions are too wide for the multi-column grid.
 *
 * It also tags the individual expressions that are too wide even for the
 * single-column layout with `mcq-math-overflow`, so the stylesheet can scope
 * scroll affordances (fade mask, end padding) to just those expressions.
 *
 * Ideally we want to have all MCQ option content to render responsively in a grid layout,
 * however MathJax does not currently support automatic line wrapping in V2.
 */
export const useMathJaxOverflow = () => {
  const ref = useRef<HTMLDivElement>(null);
  const [hasMathJaxOverflow, setHasMathJaxOverflow] = useState(false);

  useLayoutEffect(() => {
    const container = ref.current;
    if (!container) {
      return;
    }

    let timeoutId: ReturnType<typeof setTimeout> | null = null;

    const checkOverflow = () => {
      const mathJaxElements = container.querySelectorAll('.MathJax, [class^="mjx"], code');

      container.querySelectorAll(`.${MATH_OVERFLOW_CLASS}`).forEach((el) => {
        el.classList.remove(MATH_OVERFLOW_CLASS);
      });

      if (mathJaxElements.length === 0) {
        setHasMathJaxOverflow(false);
        return;
      }

      // Calculate what the column width would be in a multi-column grid layout
      // Grid: repeat(auto-fit, minmax(min(260px, 100%), 1fr)) with 16px gap
      const containerWidth = container.clientWidth;
      const gridGap = 16;

      // Determine how many columns would fit
      // The grid uses auto-fit with minmax(min(260px, 100%), 1fr)
      const numColumns = Math.max(
        1,
        Math.floor((containerWidth + gridGap) / (GRID_WIDTH_BREAKPOINT + gridGap))
      );

      const columnWidth = (containerWidth - gridGap * (numColumns - 1)) / numColumns;

      // Estimate available content width within an option
      const availableContentWidth = columnWidth - OPTION_NON_CONTENT_WIDTH;
      const stackedContentWidth = containerWidth - OPTION_NON_CONTENT_WIDTH;

      let hasOverflow = false;
      mathJaxElements.forEach((el) => {
        const mathJaxWidth = el.getBoundingClientRect().width;
        // If the MathJax element is wider than what would fit in a multi-column layout
        if (mathJaxWidth > availableContentWidth) {
          hasOverflow = true;
        }
        // Too wide even for the single-column layout: this element will scroll.
        // Code inside <pre> scrolls within the pre itself, not at the option level.
        if (mathJaxWidth > stackedContentWidth && !el.closest('pre')) {
          (el.closest('.tex2jax_process') ?? el).classList.add(MATH_OVERFLOW_CLASS);
        }
      });

      setHasMathJaxOverflow(hasOverflow);
    };

    const debouncedCheck = () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
      timeoutId = setTimeout(checkOverflow, 100);
    };

    // Watch for MathJax rendering completion
    // MathJax adds elements with specific classes when done
    const observer = new MutationObserver((mutations) => {
      const hasMathJaxMutation = mutations.some((mutation) => {
        const hasAddedMathJax = Array.from(mutation.addedNodes).some((node) => {
          if (!(node instanceof Element)) return false;
          return (
            node.classList?.contains('MathJax') ||
            node.querySelector?.('.MathJax, [class^="mjx"], code')
          );
        });
        return hasAddedMathJax;
      });

      if (hasMathJaxMutation) {
        debouncedCheck();
      }
    });

    observer.observe(container, {
      childList: true,
      subtree: true
    });

    const resizeObserver = new ResizeObserver(debouncedCheck);
    resizeObserver.observe(container);

    // Initial check after MathJax has time to render
    checkOverflow();

    return () => {
      observer.disconnect();
      resizeObserver.disconnect();
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, []);

  return {ref, hasMathJaxOverflow};
};
